import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent } from "@/components/ui/card"
import { Dialog, DialogContent, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { router } from "@inertiajs/react"
import type { ChecklistItem } from "@/types"

const STATUS_LABELS: Record<string, string> = {
  pending: "A faire",
  in_progress: "En cours",
  done: "Terminé",
}

const STATUS_VARIANTS: Record<string, "outline" | "secondary" | "default"> = {
  pending: "outline",
  in_progress: "secondary",
  done: "default",
}

type Props = {
  projectId: string
  checklistItems: ChecklistItem[]
}

export function ChecklistTab({ projectId, checklistItems }: Props) {
  const [showForm, setShowForm] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    title: "",
    description: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/projects/${projectId}/checklist_items`, {
      onSuccess: () => {
        reset()
        setShowForm(false)
      },
    })
  }

  const updateStatus = (item: ChecklistItem, status: string) => {
    router.patch(`/projects/${projectId}/checklist_items/${item.id}`, { status })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Checklist</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvel élément</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
            <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="checklist-title">Titre</Label>
              <Input
                id="checklist-title"
                value={data.title}
                onChange={(e) => setData("title", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="checklist-description">Description</Label>
              <Textarea
                id="checklist-description"
                value={data.description}
                onChange={(e) => setData("description", e.target.value)}
                rows={2}
              />
            </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {checklistItems.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucun élément dans la checklist</p>
      ) : (
        <div className="space-y-2">
          {checklistItems.map((item) => (
            <Card key={item.id}>
              <CardContent className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p
                      className={`text-sm font-medium ${item.status === "done" ? "line-through text-muted-foreground" : ""}`}
                    >
                      {item.title}
                    </p>
                    <Badge variant={STATUS_VARIANTS[item.status]} className="text-xs">
                      {STATUS_LABELS[item.status]}
                    </Badge>
                  </div>
                  {item.description && <p className="text-muted-foreground text-xs mt-1">{item.description}</p>}
                </div>
                <Select value={item.status} onValueChange={(v) => updateStatus(item, v)}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">A faire</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="done">Terminé</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  onClick={() => router.delete(`/projects/${projectId}/checklist_items/${item.id}`)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
